import axios from "axios";
import { cloneDeep } from "lodash";
import { toast } from "react-toastify";

/* -------------------- INITIAL STATE -------------------- */
const INITIAL_STATE = {
  loading: false,
  error: null,
  files: [],
  searchFiles: [],
  // The raw text currently in the main files search box. Stored in redux (not
  // just left in the input's own DOM value) so it survives the FileList
  // component unmounting/remounting when navigating away and back — e.g. to
  // Home and back to Files — matching the fact that searchFiles itself (the
  // filtered results) already persists across that same remount.
  searchTerm: "",
  unflattenedFiles: [],
  unflattenedSearchFiles: [],
  baseFiles: [],
  sasToken: "",
  editFilePath: "",
  editFileTreeIndex: -1,
  creatingFiles: false,
  // Bumped by every local/optimistic change to the unflattened tree (rename,
  // rollback). A pending "fetch unflattened list" request captures this value
  // when it starts; if it resolves after a newer local change has landed, its
  // (now stale) response is discarded instead of clobbering that change. See
  // FETCH_UNFLATTENED_FILE_LIST_SUCCESS below.
  unflattenedVersion: 0,
};

/* -------------------- ACTION TYPES -------------------- */
const FETCH_FILE_LIST_BEGIN = "FETCH_FILE_LIST_BEGIN";
const FETCH_FILE_LIST_SUCCESS = "FETCH_FILE_LIST_SUCCESS";
const FETCH_FILE_LIST_FAILURE = "FETCH_FILE_LIST_FAILURE";
const SET_SEARCH_FILES = "SET_SEARCH_FILES";
const SET_EDIT_FILE = "SET_EDIT_FILE";
const FETCH_CREATE_FILES_BEGIN = "FETCH_CREATE_FILES_BEGIN";
const FETCH_CREATE_FILES_END = "FETCH_CREATE_FILES_END";
const FETCH_UNFLATTENED_FILE_LIST_SUCCESS = "FETCH_UNFLATTENED_FILE_LIST_SUCCESS";
const SET_UNFLATTENED_FILE_LIST = "SET_UNFLATTENED_FILE_LIST";
const RENAME_FOLDER_PATHS = "RENAME_FOLDER_PATHS";
const SET_FILE_SEARCH_TERM = "SET_FILE_SEARCH_TERM";
//const DELETE_FILE_SUCCESS = "DELETE_FILE_SUCCESS";

/* -------------------- ACTION CREATORS -------------------- */

const fetchFileListBegin = () => ({
  type: FETCH_FILE_LIST_BEGIN,
});
const fetchFileListSuccess = (files) => {
  return { type: FETCH_FILE_LIST_SUCCESS, payload: files };
};
export const fetchUnFlattenedFileListSuccess = (files, baseVersion) => {
  return { type: FETCH_UNFLATTENED_FILE_LIST_SUCCESS, payload: files, baseVersion };
};
const setSearchFiles = (files) => {
  return { type: SET_SEARCH_FILES, payload: files };
};
const doSetFileSearchTerm = (term) => ({ type: SET_FILE_SEARCH_TERM, payload: term });

const fetchFileListError = (error) => ({
  type: FETCH_FILE_LIST_FAILURE,
  payload: { error },
});

const doSetEditFile = (path, ti) => ({
  type: SET_EDIT_FILE,
  payload: path,
  ti,
});
const fetchCreateFilesBegin = () => ({
  type: FETCH_CREATE_FILES_BEGIN,
});
const fetchCreateFilesEnd = () => ({
  type: FETCH_CREATE_FILES_END,
});
export const doSetUnflattenedFileList = (files, search = false) => ({
  type: SET_UNFLATTENED_FILE_LIST,
  payload: files,
  search,
});

// Optimistically rewrite a folder's name/path (and all descendant paths) in the
// unflattened trees so the UI reflects a rename instantly. Dispatch the inverse
// (renameFolderPaths(newPath, oldPath)) to revert if the server call fails.
export const renameFolderPaths = (oldPath, newPath) => ({
  type: RENAME_FOLDER_PATHS,
  oldPath,
  newPath,
});

function searchFilter(newFolders, filter, modal) {
  return newFolders.map((newFolder) => {
    if (newFolder.children === undefined) {
      if (newFolder.type === "file") {
        return null;
      } else {
        newFolder.reports = newFolder.reports.filter((report) => {
          return newFolder.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
        });
      }
    } else {
      newFolder.children = newFolder.children.filter((folder, index) => {
        if (folder.children === undefined && folder.type === "folder") {
          return folder.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
        } else if (folder.children === undefined) {
          return null;
        } else {
          folder.children = folder.children.filter((child) => {
            if (child.fileType === "folder") {
              if (modal) {
                return child.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
              } else {
                child.children = child.children.filter((gChild) => {
                  return gChild.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
                });
                if (child.children.length > 0) {
                  return true;
                } else {
                  return false;
                }
              }
            } else {
              return child.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
            }
          });
          if (folder.children.length > 0 || folder.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1) {
            return folder;
          } else {
            return null;
          }
        }
      });
    }
    if (newFolder.children.length === 0 && newFolder.name.toLowerCase().indexOf(filter.toLowerCase()) === -1) {
      return null;
    }
    return newFolder;
  });
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
/* -------------------- ASYNC ACTION CREATORS -------------------- */
export const getFileList = (flattened = false) => {
  return (dispatch, getState) => {
    dispatch(fetchFileListBegin());
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/files/listFilesinContainer`,
        {
          flattened,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        // console.log("FETCH SUCCESS");
        const files = response.data?.response || [];
        if (files.length <= 0) {
          dispatch(fetchFileListError("There are no files in this client"));
        } else {
          dispatch(fetchFileListSuccess(files));
          const baseVersion = getState().files.unflattenedVersion;
          axios
            .post(
              `${process.env.REACT_APP_BIGHORN_SERVER}/api/files/listFilesinContainer`,
              {
                flattened: false,
              },
              {
                headers: {
                  ClientToken: localStorage.getItem("clientToken"),
                },
              },
            )
            .then((response) => {
              dispatch(fetchUnFlattenedFileListSuccess(response.data?.response || [], baseVersion));
            });
        }
      })
      .catch(async (error) => {
        // console.log("FETCH ERROR Trying again");
        // dispatch(fetchFileListError(error));
        await sleep(2000);
        axios
          .post(
            `${process.env.REACT_APP_BIGHORN_SERVER}/api/files/listFilesinContainer`,
            {
              flattened,
            },
            {
              headers: {
                ClientToken: localStorage.getItem("clientToken"),
              },
            },
          )
          .then((response) => {
            // console.log("FETCH SUCCESS");
            const files = response.data?.response || [];
            if (files.length <= 0) {
              dispatch(fetchFileListError("There are no files in this client"));
            } else {
              dispatch(fetchFileListSuccess(files));
              const baseVersion = getState().files.unflattenedVersion;
              axios
                .post(
                  `${process.env.REACT_APP_BIGHORN_SERVER}/api/files/listFilesinContainer`,
                  {
                    flattened: false,
                  },
                  {
                    headers: {
                      ClientToken: localStorage.getItem("clientToken"),
                    },
                  },
                )
                .then((response) => {
                  dispatch(fetchUnFlattenedFileListSuccess(response.data?.response || [], baseVersion));
                });
            }
          })
          .catch((error) => {
            dispatch(fetchFileListError(error));
          });
      });
  };
};
export const getFileListNoLoadingImage = (flattened = false, getUnflatted = true) => {
  return (dispatch, getState) => {
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/files/listFilesinContainer`,
        {
          flattened,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        dispatch(fetchFileListSuccess(response.data?.response || []));
        if (getUnflatted) {
          const baseVersion = getState().files.unflattenedVersion;
          axios
            .post(
              `${process.env.REACT_APP_BIGHORN_SERVER}/api/files/listFilesinContainer`,
              {
                flattened: false,
              },
              {
                headers: {
                  ClientToken: localStorage.getItem("clientToken"),
                },
              },
            )
            .then((response) => {
              dispatch(fetchUnFlattenedFileListSuccess(response.data?.response || [], baseVersion));
            });
        }
      })
      .catch((error) => {
        //dispatch(fetchFileListError(error));
      });
  };
};
export const setFileList = (files) => {
  return (dispatch) => {
    //dispatch(fetchFileListBegin());
    dispatch(setSearchFiles(files.treeData));
  };
};
export const setUnflattenedFileList = (files, search = false) => {
  return (dispatch) => {
    dispatch(doSetUnflattenedFileList(files, search));
  };
};
export const updateFolderStatus = (folder) => {
  return (dispatch) => {
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/files/updateFolderStatus`,
        { folder },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {});
  };
};
export const renameFile = (srcUri, target, folder = false, getUnflattened = true) => {
  return (dispatch) => {
    return axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/files/renameFile`,
        {
          srcUri,
          target,
          isFolder: folder,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        const ok = !response.data || response.data.success !== false;
        if (ok) {
          dispatch(getFileListNoLoadingImage(true, getUnflattened));
        }
        return ok;
      })
      .catch(() => false);
  };
};

export const deleteFile = (srcUri, isFolder = false) => {
  return (dispatch) => {
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/files/deleteFile`,
        {
          srcUri,
          isFolder,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        console.log(response);
        dispatch(getFileListNoLoadingImage(true));
        toast.success(isFolder ? "Folder deleted successfully" : "File deleted successfully");
      })
      .catch(() => {
        toast.error(isFolder ? "Failed to delete folder. Please try again." : "Failed to delete file. Please try again.");
      });
  };
};

export const bulkDeleteFiles = (files) => {
  return async (dispatch) => {
    try {
      const payload = files.map((file) => ({
        srcUri: file.path,
        isFolder: file.type === "folder",
      }));
      const response = await axios.post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/files/bulkDeleteFiles`,
        { files: payload },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      );
      dispatch(getFileListNoLoadingImage(true));
      if (response.status === 207) {
        toast.error(response.data.message || "Some items failed to delete");
      } else {
        toast.success(`${response.data.deleted} item${response.data.deleted > 1 ? "s" : ""} deleted successfully`);
      }
    } catch {
      dispatch(getFileListNoLoadingImage(true));
      toast.error("Failed to delete items. Please try again.");
    }
  };
};

export const setEditFile = (path, treeIndex) => {
  return (dispatch) => {
    dispatch(doSetEditFile(path, treeIndex));
  };
};
export const filterFiles = (filter, files) => {
  return (dispatch) => {
    // Keep searchTerm mirroring the last-applied filter text so the search box
    // can be redisplayed with the right value even if the input itself was
    // recreated (e.g. FileList remounted after navigating away and back).
    dispatch(doSetFileSearchTerm(filter));
    if (filter === "") {
      dispatch(setSearchFiles(files));
    } else {
      let newFiles = cloneDeep(files);
      let nFiles = [];

      newFiles.forEach((newFile, newFileIdx) => {
        if (newFile.children === undefined) {
          if (newFile.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1) return nFiles.push(newFile);
        } else {
          newFile.children = newFile.children.filter((file, index) => {
            if (file.children === undefined) {
              return file.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
            } else {
              return true;
            }
          });
          newFile.children.forEach((file, index) => {
            if (file.children !== undefined) {
              let tempFiles = cloneDeep(file);
              file.children = [];
              tempFiles.children.forEach((cFile, cIndex) => {
                if (cFile.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1) {
                  newFile.children[index].children.push(cFile);
                }
              });
            }
          });
          nFiles.push(newFile);
        }
      });
      dispatch(setSearchFiles(nFiles));
    }
  };
};
// Resets the main files search box back to empty and restores the unfiltered
// list. Dispatched when the Files tab is closed (see topLevelNav's
// handleTabClose) — unlike navigating to Home or another app, closing the
// tab means there's no "persistent state" to preserve, so the filter must not
// still be applied the next time Files is opened fresh.
export const clearFileSearch = () => {
  return (dispatch, getState) => {
    dispatch(doSetFileSearchTerm(""));
    dispatch(setSearchFiles(getState().files.files));
  };
};

export const createFilesFolder = (folder, oldPath = "", getUnflattened = true) => {
  return (dispatch) => {
    dispatch(fetchCreateFilesBegin());
    return axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/files/createFilesFolder`,
        {
          folder,
          oldPath,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        const ok = !response.data || response.data.success !== false;
        if (ok) {
          dispatch(getFileListNoLoadingImage(true, getUnflattened));
        }
        dispatch(fetchCreateFilesEnd());
        return ok;
      })
      .catch(() => {
        dispatch(fetchCreateFilesEnd());
        return false;
      });
  };
};
export const filterFolders = (filter, folders, modal = false) => {
  return (dispatch) => {
    if (filter === "") {
      if (modal) {
        dispatch(setUnflattenedFileList(folders, true));
      } else {
        dispatch(setFileList(folders));
      }
    } else {
      let newFolders = cloneDeep(folders);
      if (modal) {
        const newUnflattened = searchFilter(newFolders, filter, true);
        dispatch(setUnflattenedFileList(newUnflattened, true));
        // dispatch(setUnflattenedFileList(searchFilter(newFolders, filter, true)));
      } else {
        dispatch(setFileList(searchFilter(newFolders, filter, false)));
      }
    }
  };
};

// export const deleteReport = (groupId, reportId) => {
//   return dispatch => {
//     axios
//       .post(
//         `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/deleteReport`,
//         {
//           groupId,
//           reportId
//         },
//         {
//           headers: {
//             Authorization: "Bearer " + localStorage.getItem("access_token")
//           }
//         }
//       )
//       .then(response => {
//         dispatch(deleteReportSuccess(response.data.response));
//       });
//   };
// };

/* -------------------- REDUCER -------------------- */
function isObject(value) {
  return value && typeof value === "object" && value.constructor === Object;
}
export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case FETCH_FILE_LIST_BEGIN:
      return { ...state, loading: true, error: null };
    case FETCH_UNFLATTENED_FILE_LIST_SUCCESS:
      // Discard a stale response: if a local change (rename, rollback) landed
      // after this fetch was issued, applying this now would silently revert it.
      if (action.baseVersion !== undefined && action.baseVersion !== state.unflattenedVersion) {
        return state;
      }
      return { ...state, unflattenedFiles: action.payload, unflattenedSearchFiles: action.payload };
    case FETCH_FILE_LIST_SUCCESS:
      // console.log(JSON.stringify(action.payload));

      return {
        ...state,
        files: action.payload,
        searchFiles: action.payload,
        baseFiles: action.payload,
        uploadSearchFiles: action.payload,
        loading: false,
      };
    case FETCH_FILE_LIST_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
        unflattenedSearchFiles: [],
        unflattenedFiles: [],
        reportConfig: [],
      };
    case SET_SEARCH_FILES:
      return {
        ...state,
        searchFiles: action.payload,
      };
    case SET_FILE_SEARCH_TERM:
      return {
        ...state,
        searchTerm: action.payload,
      };

    case SET_EDIT_FILE:
      return {
        ...state,
        editFilePath: action.payload,
        editFileTreeIndex: action.ti,
      };
    case FETCH_CREATE_FILES_BEGIN:
      return { ...state, creatingFiles: true };
    case FETCH_CREATE_FILES_END:
      const nUnFlattenedFiles = state.unflattenedFiles.map((item) => {
        item.editing = false;

        if (isObject(item.name)) {
          item.name = item.name.current.value;
        }
        item.path = item.name + "/";
        item.title = item.name;
        return item;
      });

      return {
        ...state,
        creatingFiles: false,
        editFilePath: "",
        editFileTreeIndex: -1,
        unflattenedFiles: nUnFlattenedFiles,
        unflattenedSearchFiles: nUnFlattenedFiles,
      };
    case SET_UNFLATTENED_FILE_LIST:
      if (action.search) {
        return { ...state, unflattenedSearchFiles: action.payload, unflattenedVersion: state.unflattenedVersion + 1 };
      } else {
        return {
          ...state,
          unflattenedFiles: action.payload,
          unflattenedSearchFiles: action.payload,
          unflattenedVersion: state.unflattenedVersion + 1,
        };
      }
    case RENAME_FOLDER_PATHS: {
      const { oldPath, newPath } = action;
      if (!oldPath || !newPath || oldPath === newPath) {
        return state;
      }
      const newName = newPath.replace(/\/+$/, "").split("/").pop();
      const rewriteTree = (nodes) => {
        if (!Array.isArray(nodes)) {
          return nodes;
        }
        return nodes.map((node) => {
          if (!node) {
            return node;
          }
          let next = node;
          if (typeof node.path === "string" && node.path.startsWith(oldPath)) {
            const suffix = node.path.slice(oldPath.length);
            next = { ...node, path: newPath + suffix };
            if (suffix === "") {
              next.name = newName;
              next.title = newName + "/";
            }
          }
          if (Array.isArray(next.children)) {
            next = next === node ? { ...node } : next;
            next.children = rewriteTree(next.children);
          }
          return next;
        });
      };
      return {
        ...state,
        unflattenedFiles: rewriteTree(state.unflattenedFiles),
        unflattenedSearchFiles: rewriteTree(state.unflattenedSearchFiles),
        unflattenedVersion: state.unflattenedVersion + 1,
      };
    }
    default:
      return state;
  }
};
